import type { UnknownOutputParams } from 'expo-router'
import { useState, type ReactNode } from 'react'
import { View, Platform, StyleSheet } from 'react-native'
import Animated from 'react-native-reanimated'

import { TEST_ID } from '@/src/general/constants/testId'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { filterSearchParams } from '@/src/general/utils/filterSearchParams'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'

import type { TGetCommunityApiData } from '../../../api/getCommunity.types'
import type { useCommunitySubscriptionsQuery } from '../../../services/useCommunitySubscriptionsQuery'
import { PublicationsList, type TPublicationsListProps } from '../../components/PublicationsList'
import { useListAnimatedScroll } from '../../hooks/useListAnimatedScroll'
import { CommunityCompactHeader } from './CommunityCompactHeader'
import { CommunityLeave } from './CommunityLeave'
import { CommunitySearchParamsShare } from './CommunitySearchParamsShare'
import { CommunityHeader } from './ListHeader/CommunityHeader'
import { ListHeader } from './ListHeader/ListHeader'

type TCommunityScreenLayoutProps = {
  id: number
  action?: string
  searchParams: UnknownOutputParams
  community: TGetCommunityApiData | undefined
  subscription: ReturnType<typeof useCommunitySubscriptionsQuery>['activeSubscription']
  listProps: Pick<
    TPublicationsListProps,
    | 'publications'
    | 'isPending'
    | 'isFetchingNextPage'
    | 'hasNextPage'
    | 'fetchNextPage'
    | 'autoScrollIndex'
    | 'ListEmptyComponent'
  >
  /** Rendered above the list, e.g. a floating action button */
  children?: ReactNode
}

/** Shared shell of the community page: animated header, publications list and leave modal */
export const CommunityScreenLayout = ({
  id,
  action,
  searchParams,
  community,
  subscription,
  listProps,
  children,
}: TCommunityScreenLayoutProps): ReactNode => {
  const [showCommunityLeaveModal, setShowCommunityLeaveModal] = useState(false)
  const { xs, sm } = useDimensions()
  const { redirect } = filterSearchParams(searchParams)
  const {
    headerAnimatedStyle,
    scrollHandler,
    offsetY,
    listRef,
    handleScrollBegin,
    handleScrollEnd,
    handleScrollEndDrag,
    fixedOffsetY,
  } = useListAnimatedScroll()

  const communityHeader = (
    <CommunityHeader
      community={community}
      onClickLeaveCommunity={() => setShowCommunityLeaveModal(true)}
      redirect={redirect}
      sharing={{
        picture: community?.picture,
        name: community?.name,
        trackingSource: TRACKING_SOURCE.COMMUNITY_PAGE,
      }}
    />
  )

  const mobileViewPortStyle = [
    { zIndex: 20 },
    communityStyle.mobileHeader,
    communityStyle.bottomRadius,
    Platform.OS !== 'web' ? headerAnimatedStyle : {},
  ]

  const isMobileViewPort = xs || sm

  return (
    <>
      <View testID={TEST_ID.COMMUNITY.SCREEN} className="min-w-screen h-full flex-1">
        {community && <CommunitySearchParamsShare {...community} searchParams={searchParams} />}

        <CommunityCompactHeader
          community={community}
          header={communityHeader}
          offsetY={offsetY}
          searchParams={searchParams}
          subscription={subscription}
        />

        <PublicationsList
          {...listProps}
          listRef={listRef}
          community={community}
          subscription={subscription}
          onScroll={isMobileViewPort ? scrollHandler : undefined}
          stickyHeaderIndices={Platform.OS === 'web' ? [] : [0]}
          ListHeaderComponent={
            <>
              <Animated.View
                className={!isMobileViewPort ? 'h-[420px] web:max-w-3xl web:w-3/4 web:w-full web:self-center z-30' : ''}
                style={isMobileViewPort ? mobileViewPortStyle : []}
              >
                <ListHeader
                  community={community}
                  offsetY={isMobileViewPort ? offsetY : fixedOffsetY}
                  searchParams={searchParams}
                  subscription={subscription}
                  animated={isMobileViewPort && Platform.OS !== 'web'}
                />
              </Animated.View>
              <View className="absolute z-40 w-full">{communityHeader}</View>
            </>
          }
          onMomentumScrollEnd={handleScrollEnd}
          onMomentumScrollBegin={handleScrollBegin}
          onScrollBeginDrag={handleScrollBegin}
          onScrollEndDrag={handleScrollEndDrag}
          onTouchStart={Platform.OS === 'web' ? handleScrollBegin : undefined}
          onTouchEnd={Platform.OS === 'web' ? handleScrollEnd : undefined}
        />
        {children}
      </View>
      <CommunityLeave
        id={id}
        action={action}
        showCommunityLeaveModal={showCommunityLeaveModal}
        setShowCommunityLeaveModal={setShowCommunityLeaveModal}
        community={community}
      />
    </>
  )
}

const communityStyle = StyleSheet.create({
  mobileHeader: {
    zIndex: 20,
    right: 0,
    left: 0,
    backgroundColor: 'white',
    boxShadow: '4px 0px 8px 0px rgba(0, 0, 0, 0.65)',
    height: Platform.OS === 'web' ? 420 : 400,
  },
  bottomRadius: {
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
})
